import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { TegelModule } from '@scania/tegel-angular-17';
import Swal from 'sweetalert2';
import { FullReservation } from '../../core/models/reservation/full-reservation';
import { ReservationService } from '../../core/services/application/flexseat/reservation.service';
import { DateService } from '../../core/services/application/util/date/date.service';
import { GraphService } from '../../core/services/application/util/graph/graph.service';
@Component({
  selector: 'app-history',
  imports: [TegelModule, DatePipe],
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss'
})
export class HistoryComponent {

  private readonly reservationService = inject(ReservationService);
  private readonly graphService = inject(GraphService);
  private readonly dateService = inject(DateService);

  today(): string {
    const now = this.dateService.getCurrentDate();
    return now.toISOString().split('T')[0]; // Returns only YYYY-MM-DD
  }

  /** Checks if it allows to cancel reservation on the history table */
  canCancelReservation(reservationDate: Date | string | null): boolean {
    if (!reservationDate) return false;
    
    const reservationDateObj = new Date(reservationDate);
    const today = this.dateService.getCurrentDate();
    
    /**  Reset the hours to compare only dates */
    reservationDateObj.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    return reservationDateObj >= today;
  }

  data = this.reservationService.userReservations;

  //#region [TablePagination]
  
    filteredData = signal<FullReservation[]>([...this.data()]);
    page = signal<number>(1);
    rowsPerPage = signal<number>(10);

    constructor() {
      /**  Effect - > To monitor data change and updates automatically */
      effect(() => {
        const currentData = this.data();
        this.refreshFilteredData();
      });
    }
    
    /** Get the history data to show on the page */
    tableData = computed(() => {
      const startIndex = (this.page() - 1) * this.rowsPerPage();
      const endIndex = startIndex + this.rowsPerPage();
      return this.filteredData().slice(startIndex, endIndex);
    });
    
    pages = computed(() => 
      Math.ceil(this.filteredData().length / this.rowsPerPage())
    );

    rowsPerPageValuesArray = [10, 20];
    currentSortKey: string = '';
    currentSortDirection: 'asc' | 'desc' = 'asc';
    currentFilter: string = '';

    /** Handles filtering of reservation data based on the user's search query */
    handleFilter(event: any) {
      const { query } = event.detail;
      this.currentFilter = query;
      
      let filtered = this.data().filter((object) =>
        Object.values(object).some((value) =>
          value != null && value.toString().toLowerCase().includes(query.toLowerCase())
        )
      );
      
      if (this.currentSortKey) {
        filtered = this.applySortingToArray(filtered);
      }
      
      this.filteredData.set(filtered);
      this.page.set(1);
    }

    /** handles pagination chenges by triggering a pagination event */
    handlePagination(event: any) {
      const { paginationValue, rowsPerPage } = event.detail;
      this.page.set(paginationValue);
      this.rowsPerPage.set(rowsPerPage);
    }

    /** Handles sorting of the data table based on the selected column and direction */
    sort(event: any) {
      const key = event.detail.columnKey;
      const direction = event.detail.sortingDirection;
      
      this.currentSortKey = key;
      this.currentSortDirection = direction;
      
      const sorted = this.applySortingToArray([...this.filteredData()]);
      this.filteredData.set(sorted);
      this.page.set(1);
    }

    /** Applies sorting to an array of data based on the current sort key */
    private applySortingToArray(array: any[]) {
      if (!this.currentSortKey) return array;
      
      return array.sort((a, b) => {
        let comparison = 0;
        const aVal = a[this.currentSortKey] ?? '';
        const bVal = b[this.currentSortKey] ?? '';
        
        if (aVal < bVal) comparison = -1;
        if (aVal > bVal) comparison = 1;
        
        return this.currentSortDirection === 'desc' ? comparison * -1 : comparison;
      });
    }
  
    private refreshFilteredData() {
    let filtered = this.data();
    
    // Applies filter if there's any
    if (this.currentFilter) {
      filtered = filtered.filter((object) =>
        Object.values(object).some((value) =>
          value != null && value.toString().toLowerCase().includes(this.currentFilter.toLowerCase())
        )
      );
    }
    
    /** Applies ordination if there's any */
    if (this.currentSortKey) {
      filtered = this.applySortingToArray(filtered);
    }
    
    this.filteredData.set(filtered);
    this.page.set(1);
    }
  
  //#endregion

  /** Cancels the reservation */
  async cancelReservation(reserva: FullReservation) {
    const confirmation = await Swal.fire({
      title: 'Deseja cancelar a reserva?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não'
    });

    if (confirmation.isConfirmed) {
      try {
        await this.reservationService.cancelReservationMethod(reserva, this.graphService.userGraphData()!.employeeId);
        await Swal.fire('Sucesso', 'Reserva cancelada com sucesso!', 'success');
        /** The effect() is going detect change on data() and update automatically */
      } catch (error) {
        await Swal.fire('Erro', 'Erro ao cancelar reserva. Tente novamente.', 'error');
        console.error('Erro:', error);
      }
    }
  }

  

}
